import { singleton, inject } from 'tsyringe';
import { PersistentStorageService } from '../persistent-storage-service/persistent-storage.service';
import { RemoteObjectRetrieverService } from '../remote-object/remote-object-retriever.service';
import { RawGlobalConfigurations } from './models/raw-config.model';
import { RawGame } from './models/raw-game.model';
import { RecommendationConfig } from './models/recommendation-config.model';
import { RawPromotion } from './models/raw-promotion.model';

export const kDITPromotionRemoteObjectRetrieverService =
  'rec-remote-object-ret-service';

type RemoteObjectService = RemoteObjectRetrieverService<RecommendationConfig>;

// -----------------------------------------------------------------------------
// Acts as a convenience wrapper around the raw remote configuration json.
//
// Keep this class as dumb as possible and do not convert between the raw models
// to the shared models (e.g. RawGame --> Game).
// We already have |RecommendationDatastoreService|
@singleton()
export class RemoteConfigurationsService {
  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITPromotionRemoteObjectRetrieverService)
    private readonly remoteObjectRetrieverService: RemoteObjectService,
    private readonly persistentStorage: PersistentStorageService,
  ) {
    this.getRemoteConfigs();
  }

  // ---------------------------------------------------------------------------
  public async getRawGame(gameId: number): Promise<RawGame> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.games[gameId];
  }

  // ---------------------------------------------------------------------------
  public async setNewEnvironment(
    environment: string,
    replacementString = 'prod',
  ): Promise<boolean> {
    await this.remoteObjectRetrieverService.initConfigUrl(
      this.remoteObjectRetrieverService.options.configUrl.replace(
        replacementString,
        environment,
      ),
    );

    return true;
  }

  // ---------------------------------------------------------------------------
  public async getRawGames(): Promise<{ [key: number]: RawGame }> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.games;
  }

  // ---------------------------------------------------------------------------
  public async getRawGlobalConfigs(): Promise<RawGlobalConfigurations> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.config;
  }

  // ---------------------------------------------------------------------------
  public async getPromotionById(id: string): Promise<RawPromotion> {
    const remoteConfigs = await this.getRemoteConfigs();
    const promotion = remoteConfigs?.promotions[id];
    if (!promotion) {
      return null;
    }

    return promotion;
  }

  // ---------------------------------------------------------------------------
  public async getRawPromotions(): Promise<{ [key: number]: RawPromotion }> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.promotions;
  }

  // ---------------------------------------------------------------------------
  private async getRemoteConfigs() {
    const remoteConfigs = await this.remoteObjectRetrieverService.getObject();
    if (!remoteConfigs) {
      return this.persistentStorage.getGlobalConfiguration();
    }

    this.persistentStorage.setGlobalConfiguration(remoteConfigs);
    return remoteConfigs;
  }
}
